/**
 * Colour conversion between sRGB and OKLab/OKLCH, plus APCA contrast.
 *
 * Hand-rolled rather than pulled from culori: the engine ships to both the Nest
 * server and the browser bundle, and this is the only colour maths it needs.
 */
const clamp01 = (value) => (value < 0 ? 0 : value > 1 ? 1 : value);
export function parseHex(hex) {
    const value = hex.trim().replace(/^#/, '');
    const expanded = value.length === 3 || value.length === 4
        ? value
            .split('')
            .map((char) => char + char)
            .join('')
        : value;
    if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(expanded))
        return null;
    return {
        r: parseInt(expanded.slice(0, 2), 16) / 255,
        g: parseInt(expanded.slice(2, 4), 16) / 255,
        b: parseInt(expanded.slice(4, 6), 16) / 255,
    };
}
export function toHex({ r, g, b }) {
    const channel = (value) => Math.round(clamp01(value) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${channel(r)}${channel(g)}${channel(b)}`;
}
const srgbToLinear = (channel) => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
const linearToSrgb = (channel) => channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055;
/** Björn Ottosson's OKLab transform. */
export function rgbToOklch(rgb) {
    const r = srgbToLinear(clamp01(rgb.r));
    const g = srgbToLinear(clamp01(rgb.g));
    const b = srgbToLinear(clamp01(rgb.b));
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
    const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
    const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
    const chroma = Math.sqrt(a * a + bb * bb);
    const hue = chroma < 1e-6 ? 0 : ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;
    return { l: lightness, c: chroma, h: hue };
}
export function oklchToRgb({ l, c, h }) {
    const hueRad = (h * Math.PI) / 180;
    const a = c * Math.cos(hueRad);
    const b = c * Math.sin(hueRad);
    const lCube = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
    const mCube = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
    const sCube = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
    return {
        r: clamp01(linearToSrgb(4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube)),
        g: clamp01(linearToSrgb(-1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube)),
        b: clamp01(linearToSrgb(-0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube)),
    };
}
/** True when the OKLCH triple survives a round trip without sRGB clipping. */
export function isInSrgbGamut({ l, c, h }) {
    const hueRad = (h * Math.PI) / 180;
    const a = c * Math.cos(hueRad);
    const b = c * Math.sin(hueRad);
    const lCube = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
    const mCube = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
    const sCube = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
    const channels = [
        4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube,
        -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube,
        -0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube,
    ];
    return channels.every((channel) => channel >= -1e-4 && channel <= 1 + 1e-4);
}
/**
 * Pulls chroma down until the colour fits in sRGB, keeping lightness and hue.
 * Clipping RGB directly instead would shift the hue, which is very visible on
 * saturated brand colours.
 */
export function clampToSrgbGamut(color) {
    if (isInSrgbGamut(color))
        return color;
    let low = 0;
    let high = color.c;
    for (let i = 0; i < 24; i += 1) {
        const mid = (low + high) / 2;
        if (isInSrgbGamut({ ...color, c: mid }))
            low = mid;
        else
            high = mid;
    }
    return { ...color, c: low };
}
export function hexToOklch(hex) {
    const rgb = parseHex(hex);
    return rgb ? rgbToOklch(rgb) : null;
}
export function oklchToHex(color) {
    return toHex(oklchToRgb(clampToSrgbGamut(color)));
}
// --- Contrast --------------------------------------------------------------
const APCA = {
    exponent: 2.4,
    redCoefficient: 0.2126729,
    greenCoefficient: 0.7151522,
    blueCoefficient: 0.072175,
    normBg: 0.56,
    normText: 0.57,
    revText: 0.62,
    revBg: 0.65,
    blackThreshold: 0.022,
    blackClamp: 1.414,
    scale: 1.14,
    lowOffset: 0.027,
    lowClip: 0.1,
};
function screenLuminance({ r, g, b }) {
    const y = APCA.redCoefficient * r ** APCA.exponent +
        APCA.greenCoefficient * g ** APCA.exponent +
        APCA.blueCoefficient * b ** APCA.exponent;
    return y < APCA.blackThreshold ? y + (APCA.blackThreshold - y) ** APCA.blackClamp : y;
}
/**
 * APCA lightness contrast (Lc), roughly -108..106.
 *
 * Preferred over the WCAG 2 ratio because menus lean heavily on large display
 * type over tinted backgrounds, exactly the case where the WCAG formula is
 * known to be wrong in both directions.
 *
 * Rules of thumb: |Lc| >= 90 body text, >= 75 sub-headings, >= 60 large text,
 * >= 45 large display type, >= 30 non-text UI.
 */
export function apcaContrast(textHex, backgroundHex) {
    const text = parseHex(textHex);
    const background = parseHex(backgroundHex);
    if (!text || !background)
        return 0;
    const yText = screenLuminance(text);
    const yBackground = screenLuminance(background);
    if (Math.abs(yBackground - yText) < 0.0005)
        return 0;
    if (yBackground > yText) {
        const sapc = (yBackground ** APCA.normBg - yText ** APCA.normText) * APCA.scale;
        return sapc < APCA.lowClip ? 0 : (sapc - APCA.lowOffset) * 100;
    }
    const sapc = (yBackground ** APCA.revBg - yText ** APCA.revText) * APCA.scale;
    return sapc > -APCA.lowClip ? 0 : (sapc + APCA.lowOffset) * 100;
}
/** Classic WCAG 2.1 ratio, kept for reporting because auditors still ask for it. */
export function wcagContrast(foregroundHex, backgroundHex) {
    const relativeLuminance = (hex) => {
        const rgb = parseHex(hex);
        if (!rgb)
            return 0;
        return (0.2126 * srgbToLinear(rgb.r) + 0.7152 * srgbToLinear(rgb.g) + 0.0722 * srgbToLinear(rgb.b));
    };
    const a = relativeLuminance(foregroundHex);
    const b = relativeLuminance(backgroundHex);
    const lighter = Math.max(a, b);
    const darker = Math.min(a, b);
    return (lighter + 0.05) / (darker + 0.05);
}
//# sourceMappingURL=oklch.js.map